import { useEffect, useMemo, useState } from "react";
import {
  api,
  formatCurrency,
  formatNumber,
  formatDate,
} from "@/magazzino/lib/apiClient";
import { Card } from "@/components/ui/card";
import {
  Package,
  CurrencyEur,
  Warning,
  ArrowsLeftRight,
  ArrowDown,
  ArrowUp,
  TrendUp,
  ClockCounterClockwise,
  CheckCircle,
} from "@phosphor-icons/react";
import {
  LineChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
} from "recharts";

function Kpi({
  label,
  value,
  icon: Icon,
  hint,
  tone = "default",
  testid,
}) {
  const tones = {
    default: {
      icon: "bg-slate-100 text-slate-700",
      value: "text-slate-950",
    },
    danger: {
      icon: "bg-red-50 text-red-600",
      value: "text-red-600",
    },
    accent: {
      icon: "bg-blue-50 text-blue-600",
      value: "text-blue-600",
    },
    success: {
      icon: "bg-emerald-50 text-emerald-600",
      value: "text-emerald-600",
    },
  };

  const selected = tones[tone] || tones.default;

  return (
    <Card
      className="rounded-2xl border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
      data-testid={testid}
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">
            {label}
          </div>

          <div
            className={`mt-3 text-3xl font-bold tracking-tight tabular ${selected.value}`}
          >
            {value}
          </div>

          {hint && (
            <div className="mt-2 text-sm text-slate-500">
              {hint}
            </div>
          )}
        </div>

        <div
          className={`grid h-11 w-11 shrink-0 place-items-center rounded-2xl ${selected.icon}`}
        >
          <Icon weight="fill" size={22} />
        </div>
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api
      .get("/dashboard/stats")
      .then(({ data }) => setStats(data))
      .catch(() => setStats({}));
  }, []);

  const weeklyMovements = useMemo(() => {
    return (stats?.chart_7d || []).reduce(
      (sum, item) => sum + (item.in || 0) + (item.out || 0),
      0
    );
  }, [stats]);

  if (!stats) {
    return (
      <div
        className="flex min-h-[320px] items-center justify-center"
        data-testid="dashboard-loading"
      >
        <div className="text-center">
          <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-blue-600" />
          <div className="text-sm font-medium text-slate-500">
            Caricamento dashboard...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 lg:h-full lg:overflow-y-auto lg:pr-2">
      {/* Introduzione */}
      <section className="overflow-hidden rounded-3xl bg-gradient-to-r from-slate-950 via-slate-900 to-blue-950 px-6 py-7 text-white shadow-xl shadow-slate-300/40 sm:px-8">
        <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-center">
          <div>
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-blue-100">
              <TrendUp size={16} weight="bold" />
              Riepilogo operativo
            </div>

            <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
              Situazione del magazzino
            </h1>

            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-300 sm:text-base">
              Consulta giacenze, valore complessivo, movimenti recenti e
              articoli sotto scorta minima.
            </p>
          </div>

          <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/10 px-4 py-3 backdrop-blur">
            <CheckCircle size={24} weight="fill" className="text-emerald-300" />
            <div>
              <div className="text-sm font-semibold">Sistema operativo</div>
              <div className="text-xs text-slate-300">
                Dati aggiornati in tempo reale
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* KPI */}
      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Kpi
          label="Articoli totali"
          value={formatNumber(stats.total_items)}
          icon={Package}
          hint={`${formatNumber(stats.total_units)} unità disponibili`}
          testid="kpi-total-items"
        />

        <Kpi
          label="Valore magazzino"
          value={formatCurrency(stats.stock_value)}
          icon={CurrencyEur}
          tone="accent"
          hint="Quantità moltiplicata per prezzo"
          testid="kpi-stock-value"
        />

        <Kpi
          label="Scorta minima"
          value={formatNumber(stats.low_stock_count)}
          icon={Warning}
          tone={stats.low_stock_count > 0 ? "danger" : "success"}
          hint={
            stats.low_stock_count > 0
              ? "Articoli da riordinare"
              : "Nessuna criticità"
          }
          testid="kpi-low-stock"
        />

        <Kpi
          label="Movimenti ultimi 7 giorni"
          value={formatNumber(weeklyMovements)}
          icon={ArrowsLeftRight}
          tone="success"
          hint="Totale carichi e scarichi"
          testid="kpi-movements-week"
        />
      </section>

      {/* Grafico e scorte */}
      <section className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <Card
          className="rounded-3xl border-slate-200 bg-white p-6 shadow-sm xl:col-span-2"
          data-testid="movements-chart"
        >
          <div className="mb-6 flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
            <div>
              <div className="text-xl font-bold tracking-tight text-slate-950">
                Movimenti ultimi 7 giorni
              </div>
              <div className="mt-1 text-sm text-slate-500">
                Confronto tra carichi e scarichi
              </div>
            </div>

            <div className="flex gap-4 text-xs font-medium">
              <span className="flex items-center gap-2 text-emerald-700">
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
                Carico
              </span>

              <span className="flex items-center gap-2 text-amber-700">
                <span className="h-2.5 w-2.5 rounded-full bg-amber-500" />
                Scarico
              </span>
            </div>
          </div>

          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={stats.chart_7d || []}>
                <CartesianGrid
                  stroke="#e2e8f0"
                  strokeDasharray="4 4"
                  vertical={false}
                />

                <XAxis
                  dataKey="date"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: "#64748b" }}
                  tickFormatter={(date) => date?.slice(5)}
                />

                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: "#64748b" }}
                />

                <Tooltip
                  contentStyle={{
                    background: "#ffffff",
                    border: "1px solid #e2e8f0",
                    borderRadius: 14,
                    fontSize: 12,
                    boxShadow: "0 10px 30px rgba(15, 23, 42, 0.08)",
                  }}
                />

                <Legend wrapperStyle={{ fontSize: 12 }} />

                <Line
                  type="monotone"
                  dataKey="in"
                  name="Carico"
                  stroke="#10b981"
                  strokeWidth={3}
                  dot={{ r: 4, fill: "#10b981", strokeWidth: 0 }}
                  activeDot={{ r: 6 }}
                />

                <Line
                  type="monotone"
                  dataKey="out"
                  name="Scarico"
                  stroke="#f59e0b"
                  strokeWidth={3}
                  dot={{ r: 4, fill: "#f59e0b", strokeWidth: 0 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card
          className="rounded-3xl border-slate-200 bg-white p-6 shadow-sm"
          data-testid="low-stock-panel"
        >
          <div className="mb-5 flex items-start justify-between">
            <div>
              <div className="text-xl font-bold tracking-tight text-slate-950">
                Scorta minima
              </div>
              <div className="mt-1 text-sm text-slate-500">
                Articoli sotto soglia
              </div>
            </div>

            <div className="grid h-10 w-10 place-items-center rounded-xl bg-red-50 text-red-600">
              <Warning size={21} weight="fill" />
            </div>
          </div>

          {!stats.low_stock || stats.low_stock.length === 0 ? (
            <div className="flex min-h-[220px] flex-col items-center justify-center rounded-2xl border border-dashed border-emerald-200 bg-emerald-50/50 px-5 text-center">
              <CheckCircle
                size={34}
                weight="fill"
                className="mb-3 text-emerald-500"
              />
              <div className="font-semibold text-emerald-800">
                Tutto sotto controllo
              </div>
              <div className="mt-1 text-sm text-emerald-700">
                Nessun articolo è sotto la soglia minima.
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {stats.low_stock.map((product) => (
                <div
                  key={product.id}
                  className="flex items-center justify-between gap-4 rounded-2xl border border-slate-100 bg-slate-50 p-4"
                >
                  <div className="min-w-0">
                    <div className="truncate font-semibold text-slate-900">
                      {product.name}
                    </div>
                    <div className="mt-1 text-xs text-slate-500 tabular">
                      SKU: {product.sku}
                    </div>
                  </div>

                  <div className="shrink-0 text-right">
                    <div className="font-bold text-red-600 tabular">
                      {product.quantity} {product.unit}
                    </div>
                    <div className="text-xs text-slate-500">
                      minimo {product.min_stock}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </section>

      {/* Movimenti recenti */}
      <section>
        <Card
          className="overflow-hidden rounded-3xl border-slate-200 bg-white shadow-sm"
          data-testid="recent-movements"
        >
          <div className="flex flex-col justify-between gap-3 border-b border-slate-200 px-6 py-5 sm:flex-row sm:items-center">
            <div>
              <div className="text-xl font-bold tracking-tight text-slate-950">
                Movimenti recenti
              </div>
              <div className="mt-1 text-sm text-slate-500">
                Ultime operazioni effettuate nel magazzino
              </div>
            </div>

            <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
              <ClockCounterClockwise size={17} weight="bold" />
              Aggiornamento automatico
            </div>
          </div>

          {!stats.recent_movements ||
          stats.recent_movements.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-500">
              Nessun movimento registrato.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px] text-sm">
                <thead>
                  <tr className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                    <th className="px-6 py-4">Data</th>
                    <th className="px-6 py-4">Tipo</th>
                    <th className="px-6 py-4">Prodotto</th>
                    <th className="px-6 py-4 text-right">Quantità</th>
                    <th className="px-6 py-4">Operatore</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-100">
                  {stats.recent_movements.map((movement) => (
                    <tr
                      key={movement.id}
                      className="transition hover:bg-slate-50"
                    >
                      <td className="whitespace-nowrap px-6 py-4 text-slate-600 tabular">
                        {formatDate(movement.created_at)}
                      </td>

                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${
                            movement.type === "in"
                              ? "bg-emerald-50 text-emerald-700"
                              : "bg-amber-50 text-amber-700"
                          }`}
                        >
                          {movement.type === "in" ? (
                            <ArrowDown size={13} weight="bold" />
                          ) : (
                            <ArrowUp size={13} weight="bold" />
                          )}

                          {movement.type === "in"
                            ? "Carico"
                            : "Scarico"}
                        </span>
                      </td>

<td className="px-6 py-4">
                        <div>
                          <div className="font-semibold text-slate-900">
                            {movement.product_name || "Prodotto non trovato"}
                          </div>

                          {movement.product_sku && (
                            <div className="mt-1 text-xs text-slate-500">
                              SKU: {movement.product_sku}
                            </div>
                          )}
                        </div>
                      </td>

                      <td className="px-6 py-4 text-right font-bold text-slate-900 tabular">
                        {movement.quantity}
                      </td>

                      <td className="max-w-[220px] truncate px-6 py-4 text-slate-600">
                        {movement.user_name || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </section>
    </div>
  );
}

